<script setup lang="ts">
/**
 * Skeleton-rows placeholder for any admin panel that renders an
 * async-loaded list. Shared so every panel uses the same shimmer
 * shape — gives the admin section a consistent "is loading" tell
 * instead of four different "Loading…" strings.
 *
 * Default: 5 rows. Each row is 3 column-ish bars whose widths vary
 * just enough to look real, with a single shimmer animation that
 * honours prefers-reduced-motion.
 */
const props = defineProps<{ rows?: number }>();
const count = props.rows ?? 5;
const widths = ["60%", "85%", "30%"];
</script>

<template>
  <div class="skeleton-rows" aria-busy="true" aria-live="polite">
    <div v-for="r in count" :key="r" class="skeleton-row">
      <span
        v-for="(w, i) in widths"
        :key="i"
        class="skeleton-bar"
        :style="{ width: w }"
      />
    </div>
  </div>
</template>

<style scoped>
.skeleton-rows {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  padding: 0.4rem 0;
}
.skeleton-row {
  display: flex;
  gap: 1rem;
  align-items: center;
}
.skeleton-bar {
  display: inline-block;
  height: 0.85rem;
  border-radius: 4px;
  background: linear-gradient(
    90deg,
    var(--color-border) 0%,
    var(--color-surface-alt) 50%,
    var(--color-border) 100%
  );
  background-size: 200% 100%;
  animation: skeleton-rows-shimmer 1.4s infinite linear;
}
@keyframes skeleton-rows-shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
@media (prefers-reduced-motion: reduce) {
  .skeleton-bar { animation: none; }
}
</style>
